import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import DataTable from '@/components/DataTable/DataTable';
import CpdActionButton from '@/components/ui/CpdActionButton';
import { useConfirmation } from '@/context/ConfirmationContext';
import { ReportTemplatesApi } from '@/api/clients/outbound-reporting/outbound-reporting.api';
import { SftpConnectionsApi } from '@/api/clients/sftp-connections/sftp-connections.api';

export default function ReportingList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dataTableRef = useRef(null);
  const { requestConfirmation } = useConfirmation();
  const [actionLoading, setActionLoading] = useState(false);
  const [sftpConnections, setSftpConnections] = useState({});

  useEffect(() => {
    SftpConnectionsApi.getSftpConnectionsForForm()
      .then((res) => {
        const map = {};
        (res.data || []).forEach((c) => {
          map[c.id] = c.name || c.host;
        });
        setSftpConnections(map);
      })
      .catch(console.error);
  }, []);

  const handleToggleActive = (row) => {
    const isEnabling = !row.is_active;
    requestConfirmation(
      isEnabling
        ? t('reporting.list.confirm.enable', { name: row.reporting_name })
        : t('reporting.list.confirm.disable', { name: row.reporting_name }),
      async () => {
        setActionLoading(true);
        try {
          if (isEnabling) {
            await ReportTemplatesApi.enableReportTemplate(row.id);
          } else {
            await ReportTemplatesApi.disableReportTemplate(row.id);
          }
          dataTableRef.current?.refresh();
        } finally {
          setActionLoading(false);
        }
      },
    );
  };

  const handleDelete = (row) => {
    requestConfirmation(
      t('reporting.list.confirm.delete', { name: row.reporting_name }),
      async () => {
        setActionLoading(true);
        try {
          await ReportTemplatesApi.deleteReportTemplate(row.id);
          dataTableRef.current?.refresh();
        } finally {
          setActionLoading(false);
        }
      },
    );
  };

  const columns = useMemo(
    () => [
      {
        key: 'reporting_name',
        label: t('reporting.list.columns.name'),
        sortable: true,
      },
      {
        key: 'file_type',
        label: t('reporting.list.columns.fileType'),
        sortable: true,
        render: (value) => value || '–',
      },
      {
        key: 'interval',
        label: t('reporting.list.columns.interval'),
        sortable: true,
        render: (value) => value || '–',
      },
      {
        key: 'schedule_time',
        label: t('reporting.list.columns.scheduleTime'),
        sortable: true,
        render: (value) => value || '–',
      },
      {
        key: 'client_sftp_connection_id',
        label: t('reporting.list.columns.sftpConnection'),
        sortable: false,
        render: (value) => sftpConnections[value] || value || '–',
      },
      {
        key: 'is_active',
        label: t('reporting.list.columns.status'),
        sortable: true,
        render: (value) =>
          value ? (
            <Chip
              label={t('reporting.list.status.active')}
              color='success'
              size='small'
            />
          ) : (
            <Chip
              label={t('reporting.list.status.disabled')}
              color='default'
              size='small'
            />
          ),
      },
      {
        key: 'actions',
        label: t('reporting.list.columns.actions'),
        sortable: false,
        align: 'right',
        render: (_, row) => (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
            <Tooltip title={t('reporting.list.actions.edit')}>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/facility/edit-reporting/${row.id}`);
                }}
                disabled={actionLoading}>
                <EditIcon fontSize='small' />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('reporting.list.actions.delete')}>
              <IconButton
                size='small'
                color='error'
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row);
                }}
                disabled={actionLoading}>
                <DeleteIcon fontSize='small' />
              </IconButton>
            </Tooltip>

            <Tooltip
              title={
                row.is_active
                  ? t('reporting.list.actions.disable')
                  : t('reporting.list.actions.enable')
              }>
              <Switch
                size='small'
                checked={!!row.is_active}
                disabled={actionLoading}
                onClick={(e) => e.stopPropagation()}
                onChange={() => handleToggleActive(row)}
              />
            </Tooltip>
          </Box>
        ),
      },
    ],
    [
      t,
      navigate,
      handleToggleActive,
      handleDelete,
      actionLoading,
      sftpConnections,
    ],
  );

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color='primary'>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
              <Typography variant='h6' component='h4' sx={{ color: 'white' }}>
                {t('reporting.list.title')}
              </Typography>
              <CpdActionButton
                variant='contained'
                startIcon={<AddIcon sx={{ color: 'white' }} />}
                onClick={() => navigate('/facility/create-reporting')}>
                {t('reporting.list.createButton')}
              </CpdActionButton>
            </Box>
          </CardHeader>
          <CardBody>
            <DataTable
              ref={dataTableRef}
              columns={columns}
              endpoint='/outbound-reporting'
              initialSort={{ key: 'created_at', order: 'desc' }}
              pageSize={50}
              autoFetch={true}
              enableColumnPicker={true}
              enableSorting={true}
              externalLoading={actionLoading}
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
